import type { ClaudeEvent, ClaudeUserContentBlock, ClaudeUserEvent } from "../../../providerEvents/claude"
import type { CodexCommandExecutionItem, CodexEvent, CodexRawJsonEvent } from "../../../providerEvents/codex"
import type { AgentActivity, AgentActivityEvent, AgentActivityGroup, AgentActivityStatus, AgentActivityUsage } from "../messageGroups"

export interface AgentActivityExtraction {
    group: AgentActivityGroup | null
    consumedIndices: Set<number>
    agentToolUseIds: Set<string>
}

interface AgentDraft extends AgentActivity {
    toolUseId?: string
    background?: boolean
}

const CODEX_AGENT_TOOLS = new Set(["spawn_agent", "send_message", "followup_task", "interrupt_agent", "wait_agent", "list_agents"])

export function extractClaudeAgentActivity(events: ClaudeEvent[]): AgentActivityExtraction {
    const agents: AgentDraft[] = []
    const byToolUseId = new Map<string, AgentDraft>()
    const byId = new Map<string, AgentDraft>()
    const consumedIndices = new Set<number>()
    const agentToolUseIds = new Set<string>()
    let firstIndex = Number.POSITIVE_INFINITY

    for (let index = 0; index < events.length; index++) {
        const event = events[index]
        if (event.type !== "assistant") continue

        for (const block of event.message.content) {
            if (block.type !== "tool_use" || block.name !== "Agent") continue
            const label = stringValue(block.input.description) ?? firstLine(stringValue(block.input.prompt)) ?? "Claude agent"
            const draft: AgentDraft = {
                id: block.id,
                label,
                status: "running",
                taskType: stringValue(block.input.subagent_type),
                model: stringValue(event.message.model),
                startedAt: eventTimestamp(event),
                events: [activityEvent(index, "started", label, event)],
                toolUseId: block.id,
                background: block.input.run_in_background === true,
            }
            agents.push(draft)
            byToolUseId.set(block.id, draft)
            byId.set(block.id, draft)
            agentToolUseIds.add(block.id)
            firstIndex = Math.min(firstIndex, index)
        }
    }

    if (agents.length === 0) {
        return { group: null, consumedIndices, agentToolUseIds }
    }

    for (let index = 0; index < events.length; index++) {
        const event = events[index]

        if (event.type === "user") {
            for (const result of toolResults(event)) {
                const agent = byToolUseId.get(result.toolUseId)
                if (!agent) continue
                const agentId = agentIdFromText(result.content)
                if (agentId) {
                    byId.delete(agent.id)
                    agent.id = agentId
                    byId.set(agentId, agent)
                }
                const isBackgroundLaunch = agent.background || agentId !== undefined || /async agent launched/i.test(result.content)
                if (!isBackgroundLaunch && !result.isError) agent.status = "completed"
                if (result.isError) agent.status = "failed"
                agent.events.push(activityEvent(index, result.isError ? "failed" : "launched", compactSummary(result.content), event))
                agent.updatedAt = eventTimestamp(event) ?? agent.updatedAt
            }
            continue
        }

        const queueNotification = claudeQueueNotification(event)
        if (queueNotification) {
            const agent = findClaudeAgent(queueNotification, byToolUseId, byId)
            if (!agent) continue
            applyClaudeNotification(agent, queueNotification, index, event)
            consumedIndices.add(index)
            firstIndex = Math.min(firstIndex, index)
            continue
        }

        const taskEvent = claudeTaskEvent(event)
        if (!taskEvent) continue
        const agent = findClaudeAgent(taskEvent, byToolUseId, byId)
        if (!agent) continue
        applyClaudeNotification(agent, taskEvent, index, event)
        consumedIndices.add(index)
        firstIndex = Math.min(firstIndex, index)
    }

    return {
        group: buildGroup("claude-code", agents, firstIndex),
        consumedIndices,
        agentToolUseIds,
    }
}

export function extractCodexAgentActivity(events: CodexEvent[]): AgentActivityExtraction {
    const agents: AgentDraft[] = []
    const byCallId = new Map<string, AgentDraft>()
    const byPath = new Map<string, AgentDraft>()
    const consumedIndices = new Set<number>()
    let firstIndex = Number.POSITIVE_INFINITY

    for (let index = 0; index < events.length; index++) {
        const event = events[index]
        if (event.type !== "item.started" && event.type !== "item.updated" && event.type !== "item.completed") continue
        if (event.item.type !== "command_execution" || event.item.toolName !== "spawn_agent") continue

        const input = jsonRecord(event.item.input)
        const taskName = stringValue(input?.task_name)
        const path = pathForTask(taskName)
        const output = jsonRecord(event.item.aggregated_output)
        const outputPath = stringValue(output?.task_name)
        const existing = byCallId.get(event.item.id)
        if (existing) {
            if (event.item.status === "failed") existing.status = "failed"
            if (outputPath) {
                existing.path = outputPath
                existing.label = labelFromPath(outputPath) ?? existing.label
                byPath.set(outputPath, existing)
            }
            consumedIndices.add(index)
            continue
        }
        const draft: AgentDraft = {
            id: event.item.id,
            label: taskName ?? labelFromPath(outputPath) ?? "Codex agent",
            status: event.item.status === "failed" ? "failed" : "running",
            path: outputPath ?? path,
            events: [activityEvent(index, "started", taskName, event)],
            toolUseId: event.item.id,
        }
        agents.push(draft)
        byCallId.set(event.item.id, draft)
        if (draft.path) byPath.set(draft.path, draft)
        consumedIndices.add(index)
        firstIndex = Math.min(firstIndex, index)
    }

    for (let index = 0; index < events.length; index++) {
        const event = events[index]
        if (event.type === "item.started" || event.type === "item.updated" || event.type === "item.completed") {
            if (event.item.type !== "command_execution" || !event.item.toolName || !CODEX_AGENT_TOOLS.has(event.item.toolName)) continue
            consumedIndices.add(index)
            firstIndex = Math.min(firstIndex, index)
            applyCodexToolCall(event.item, index, event, agents, byPath)
            continue
        }

        if (event.type !== "raw_json") continue
        if (event.original_type === "sub_agent_activity" || event.original_type === "item_completed:SubAgentActivity") {
            const wrapper = rawPayload(event)
            const payload = event.original_type === "item_completed:SubAgentActivity" ? recordValue(wrapper?.item) : wrapper
            const callId = stringValue(payload?.event_id)
            const itemId = stringValue(payload?.id)
            const path = stringValue(payload?.agent_path)
            let agent = (callId ? byCallId.get(callId) : undefined) ?? (itemId ? byCallId.get(itemId) : undefined) ?? (path ? byPath.get(path) : undefined)
            if (!agent) {
                agent = newCodexAgent(path, stringValue(payload?.agent_thread_id), index, event)
                agents.push(agent)
            }
            const threadId = stringValue(payload?.agent_thread_id)
            if (threadId) agent.id = threadId
            if (path) {
                agent.path = path
                agent.label = labelFromPath(path) ?? agent.label
                byPath.set(path, agent)
            }
            const kind = stringValue(payload?.kind) ?? "activity"
            agent.status = codexSubAgentStatus(kind, agent.status)
            agent.events.push(activityEvent(index, kind, path, event.raw))
            agent.updatedAt = rawTimestamp(event) ?? agent.updatedAt
            consumedIndices.add(index)
            firstIndex = Math.min(firstIndex, index)
            continue
        }

        if (event.original_type === "agent_message") {
            const payload = rawPayload(event)
            const author = stringValue(payload?.author)
            const recipient = stringValue(payload?.recipient)
            const childPath = childAgentPath(author, recipient)
            if (!childPath) continue
            let agent = byPath.get(childPath)
            if (!agent) {
                agent = newCodexAgent(childPath, undefined, index, event)
                agents.push(agent)
                byPath.set(childPath, agent)
            }
            const text = textFromContent(payload?.content)
            const messageType = headerValue(text, "Message Type")
            const summary = payloadText(text)
            if (messageType === "FINAL_ANSWER") {
                agent.status = "completed"
                agent.summary = summary
            } else if (messageType === "NEW_TASK" || messageType === "MESSAGE") {
                agent.status = "running"
                if (summary) agent.summary = summary
            }
            agent.events.push(activityEvent(index, messageType?.toLowerCase() ?? "message", summary, event.raw))
            agent.updatedAt = rawTimestamp(event) ?? agent.updatedAt
            consumedIndices.add(index)
            firstIndex = Math.min(firstIndex, index)
            continue
        }

        if (event.original_type === "inter_agent_communication_metadata" && agents.length > 0) {
            consumedIndices.add(index)
        }
    }

    return {
        group: agents.length > 0 ? buildGroup("codex", agents, firstIndex) : null,
        consumedIndices,
        agentToolUseIds: new Set<string>(),
    }
}

function codexSubAgentStatus(kind: string, current: AgentActivityStatus): AgentActivityStatus {
    switch (kind.toLowerCase()) {
        case "started":
        case "interacted":
            return "running"
        case "completed":
            return "completed"
        case "failed":
            return "failed"
        case "interrupted":
        case "stopped":
            return "stopped"
        default:
            return current
    }
}

function buildGroup(harnessId: "claude-code" | "codex", agents: AgentDraft[], firstIndex: number): AgentActivityGroup {
    const normalizedAgents = agents.map(({ toolUseId: _toolUseId, background: _background, ...agent }) => agent)
    const isPending = normalizedAgents.some((agent) => agent.status === "running")
    const hasGaps = normalizedAgents.some(
        (agent) => agent.status === "failed" || agent.status === "killed" || agent.status === "stopped" || agent.status === "unknown"
    )
    return {
        type: "agentActivity",
        harnessId,
        agents: normalizedAgents,
        isPending,
        hasGaps,
        isError: false,
        messageIndex: Number.isFinite(firstIndex) ? firstIndex : 0,
    }
}

function applyCodexToolCall(item: CodexCommandExecutionItem, index: number, raw: unknown, agents: AgentDraft[], byPath: Map<string, AgentDraft>): void {
    const toolName = item.toolName
    if (!toolName || toolName === "spawn_agent") return
    const input = jsonRecord(item.input)
    const target = stringValue(input?.target)
    if (!target || target === "/root") return
    let agent = byPath.get(target)
    if (!agent) {
        agent = newCodexAgent(target, undefined, index, raw)
        agents.push(agent)
        byPath.set(target, agent)
    }
    if (toolName === "interrupt_agent") {
        const previousStatus = interruptPreviousStatus(item.aggregated_output)
        if (previousStatus === "completed" || previousStatus === "failed") {
            agent.status = previousStatus
        } else if (item.status !== "failed") {
            agent.status = "stopped"
        }
    } else if (toolName === "send_message" || toolName === "followup_task") {
        agent.status = "running"
    }
    agent.events.push(activityEvent(index, toolName, target, raw))
}

function interruptPreviousStatus(output: string): "completed" | "failed" | undefined {
    const previousStatus = recordValue(jsonRecord(output)?.previous_status)
    if (previousStatus?.completed !== undefined) return "completed"
    if (previousStatus?.failed !== undefined) return "failed"
    return undefined
}

function newCodexAgent(path: string | undefined, id: string | undefined, index: number, raw: unknown): AgentDraft {
    return {
        id: id ?? path ?? `codex-agent-${index}`,
        label: labelFromPath(path) ?? "Codex agent",
        status: "running",
        ...(path ? { path } : {}),
        startedAt: rawTimestampFromUnknown(raw),
        events: [],
    }
}

interface ClaudeNotification {
    taskId?: string
    toolUseId?: string
    status?: string
    summary?: string
    outputFile?: string
    timestamp?: string
    usage?: AgentActivityUsage
    kind: string
}

function claudeQueueNotification(event: ClaudeEvent): ClaudeNotification | null {
    if (event.type !== "raw_json" || event.original_type !== "queue-operation") return null
    const content = stringValue(event.raw.content)
    if (!content?.includes("<task-notification>")) return null
    return {
        taskId: xmlValue(content, "task-id"),
        toolUseId: xmlValue(content, "tool-use-id"),
        status: xmlValue(content, "status"),
        summary: xmlValue(content, "summary") ?? xmlValue(content, "result"),
        outputFile: xmlValue(content, "output-file"),
        timestamp: stringValue(event.raw.timestamp),
        usage: usageFromXml(content),
        kind: "task_notification",
    }
}

function claudeTaskEvent(event: ClaudeEvent): ClaudeNotification | null {
    let raw: Record<string, unknown> | null = null
    let subtype: string | undefined
    if (event.type === "system") {
        raw = recordValue(event)
        subtype = stringValue(raw?.subtype)
    } else if (event.type === "raw_json" && event.original_type === "system") {
        raw = event.raw
        subtype = event.original_subtype
    }
    if (!raw || !subtype || !["task_started", "task_progress", "task_notification", "task_updated"].includes(subtype)) return null
    const patch = recordValue(raw.patch)
    return {
        taskId: stringValue(raw.task_id),
        toolUseId: stringValue(raw.tool_use_id),
        status: stringValue(raw.status) ?? stringValue(patch?.status),
        summary: stringValue(raw.summary) ?? stringValue(raw.description),
        outputFile: stringValue(raw.output_file),
        timestamp: stringValue(raw.timestamp),
        usage: usageFromRecord(recordValue(raw.usage)),
        kind: subtype,
    }
}

function findClaudeAgent(notification: ClaudeNotification, byToolUseId: Map<string, AgentDraft>, byId: Map<string, AgentDraft>): AgentDraft | undefined {
    return (notification.toolUseId ? byToolUseId.get(notification.toolUseId) : undefined) ?? (notification.taskId ? byId.get(notification.taskId) : undefined)
}

function applyClaudeNotification(agent: AgentDraft, notification: ClaudeNotification, index: number, raw: unknown): void {
    if (notification.taskId && agent.id !== notification.taskId) {
        agent.id = notification.taskId
    }
    if (notification.status) agent.status = normalizeStatus(notification.status)
    if (notification.summary) agent.summary = notification.summary
    if (notification.outputFile) agent.outputFile = notification.outputFile
    if (notification.usage) agent.usage = mergeUsage(agent.usage, notification.usage)
    agent.updatedAt = notification.timestamp ?? agent.updatedAt
    agent.events.push(activityEvent(index, notification.kind, notification.summary ?? notification.status, raw))
}

function toolResults(event: ClaudeUserEvent): Array<{ toolUseId: string; content: string; isError: boolean }> {
    if (!Array.isArray(event.message.content)) return []
    return event.message.content
        .filter((block): block is ClaudeUserContentBlock & { type: "tool_result" } => block.type === "tool_result")
        .map((block) => ({
            toolUseId: block.tool_use_id,
            content: contentText(block.content),
            isError: block.is_error === true,
        }))
}

function contentText(value: unknown): string {
    if (typeof value === "string") return value
    if (!Array.isArray(value)) return JSON.stringify(value)
    return value
        .map((block) => {
            const record = recordValue(block)
            return stringValue(record?.text) ?? ""
        })
        .filter(Boolean)
        .join("\n")
}

function normalizeStatus(value: string): AgentActivityStatus {
    switch (value.toLowerCase()) {
        case "running":
        case "in_progress":
        case "started":
            return "running"
        case "completed":
        case "complete":
        case "succeeded":
        case "success":
            return "completed"
        case "failed":
        case "error":
            return "failed"
        case "killed":
            return "killed"
        case "stopped":
        case "cancelled":
        case "canceled":
        case "interrupted":
            return "stopped"
        default:
            return "unknown"
    }
}

function activityEvent(messageIndex: number, kind: string, summary: string | undefined, raw: unknown): AgentActivityEvent {
    return {
        kind,
        ...(summary ? { summary: compactSummary(summary) } : {}),
        ...(rawTimestampFromUnknown(raw) ? { timestamp: rawTimestampFromUnknown(raw) } : {}),
        raw,
        messageIndex,
    }
}

function rawPayload(event: CodexRawJsonEvent): Record<string, unknown> | null {
    return recordValue(event.raw.payload)
}

function rawTimestamp(event: CodexRawJsonEvent): string | undefined {
    return stringValue(event.raw.timestamp)
}

function rawTimestampFromUnknown(value: unknown): string | undefined {
    const record = recordValue(value)
    if (!record) return undefined
    return stringValue(record.timestamp) ?? stringValue(record.updatedAt)
}

function eventTimestamp(event: ClaudeEvent): string | undefined {
    return rawTimestampFromUnknown(event)
}

function childAgentPath(author: string | undefined, recipient: string | undefined): string | undefined {
    if (author && author !== "/root" && author.startsWith("/root/")) return author
    if (recipient && recipient !== "/root" && recipient.startsWith("/root/")) return recipient
    return undefined
}

function textFromContent(value: unknown): string {
    if (!Array.isArray(value)) return ""
    return value
        .map((block) => stringValue(recordValue(block)?.text) ?? "")
        .filter(Boolean)
        .join("\n")
}

function headerValue(text: string, name: string): string | undefined {
    const prefix = `${name}:`
    const line = text.split(/\r?\n/).find((candidate) => candidate.startsWith(prefix))
    return line?.slice(prefix.length).trim() || undefined
}

function payloadText(text: string): string | undefined {
    const match = text.match(/(?:^|\n)Payload:\s*\n([\s\S]*)$/)
    return match?.[1]?.trim() || undefined
}

function pathForTask(taskName: string | undefined): string | undefined {
    if (!taskName) return undefined
    return taskName.startsWith("/") ? taskName : `/root/${taskName}`
}

function labelFromPath(path: string | undefined): string | undefined {
    return path?.split("/").filter(Boolean).at(-1)
}

function agentIdFromText(text: string): string | undefined {
    return text.match(/(?:agentId:\s*|<task-id>)([^<\s]+)/i)?.[1]
}

function firstLine(value: string | undefined): string | undefined {
    return value?.split(/\r?\n/, 1)[0]?.trim() || undefined
}

function xmlValue(content: string, tag: string): string | undefined {
    const match = content.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "i"))
    return match?.[1]?.trim() || undefined
}

function usageFromXml(content: string): AgentActivityUsage | undefined {
    return usageFromRecord({
        total_tokens: numberFromString(xmlValue(content, "subagent_tokens")),
        tool_uses: numberFromString(xmlValue(content, "tool_uses")),
        duration_ms: numberFromString(xmlValue(content, "duration_ms")),
    })
}

function usageFromRecord(record: Record<string, unknown> | null): AgentActivityUsage | undefined {
    if (!record) return undefined
    const totalTokens = numberValue(record.total_tokens) ?? numberValue(record.subagent_tokens)
    const toolUses = numberValue(record.tool_uses)
    const durationMs = numberValue(record.duration_ms)
    if (totalTokens === undefined && toolUses === undefined && durationMs === undefined) return undefined
    return {
        ...(totalTokens !== undefined ? { totalTokens } : {}),
        ...(toolUses !== undefined ? { toolUses } : {}),
        ...(durationMs !== undefined ? { durationMs } : {}),
    }
}

function mergeUsage(existing: AgentActivityUsage | undefined, next: AgentActivityUsage): AgentActivityUsage {
    const totalTokens = next.totalTokens ?? existing?.totalTokens
    const toolUses = next.toolUses ?? existing?.toolUses
    const durationMs = next.durationMs ?? existing?.durationMs
    return {
        ...(totalTokens !== undefined ? { totalTokens } : {}),
        ...(toolUses !== undefined ? { toolUses } : {}),
        ...(durationMs !== undefined ? { durationMs } : {}),
    }
}

function compactSummary(value: string): string {
    const normalized = value.replace(/\s+/g, " ").trim()
    return normalized.length > 500 ? `${normalized.slice(0, 497)}...` : normalized
}

function jsonRecord(value: string | undefined): Record<string, unknown> | null {
    if (!value) return null
    try {
        return recordValue(JSON.parse(value))
    } catch {
        return null
    }
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value : undefined
}

function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

function numberFromString(value: string | undefined): number | undefined {
    if (!value) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}
